import React, { useEffect, useState } from 'react';
import Fade from '@mui/material/Fade';
import { Button, Grid2, TextField, Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, MenuItem, Select, InputLabel, Tooltip } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import instance from '../axios/axios';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmDialog from './ConfirmDialog';


function ReportManagement() {
    const [addReport, setAddReport] = useState(false);
    const [reportName, setReportName] = useState('');
    const [reportPath, setReportPath] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [requiredFields, setRequiredFields] = useState([
        {field_name: 'start_date', field_type: 'date'},
        {field_name: 'end_date', field_type: 'date'}
    ]);
    const [allReports, setAllReports] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('info');
    const [openAddReport, setOpenAddReport] = useState(false);
    const [openAddSqlReport, setOpenAddSqlReport] = useState(false);
    const [reportQuery, setReportQuery] = useState('');
    const [sqlDatabase, setSqlDatabase] = useState('');
    const [checked, setChecked] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [editReportDialogOpen, setEditReportDialogOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [editReportName, setEditReportName] = useState('');
    const [editReportPath, setEditReportPath] = useState('');
    const [editApiKey, setEditApiKey] = useState('');
    const [editRequiredFields, setEditRequiredFields] = useState([]);
    const [editSubmitDisabled, setEditSubmitDisabled] = useState(true);
    const [addSubmitDisabled, setAddSubmitDisabled] = useState(true);

    const handleReportClear = () => {
        setReportName('');
        setReportPath('');
        setApiKey('');
        setRequiredFields([
            {field_name: 'start_date', field_type: 'date'},
            {field_name: 'end_date', field_type: 'date'}
        ]);
        setAddSubmitDisabled(true);
        setOpenAddReport(false);
    };

      const handleClick = () => {
        setOpenAddReport(!openAddReport);
    };

    const handleSqlClear = () => {
        setReportName('');
        setReportQuery('');
        setSqlDatabase('');
        setOpenAddSqlReport(false);
    };

      const handleClickSql = () => {
        setOpenAddSqlReport(!openAddSqlReport);
    };

    useEffect(() => {
        const fetchReports = async () => {
            try {
                const response = await instance.post('/reports/all', {}, {withCredentials: true});
                if (response.status === 200) {
                    setAllReports(response.data.data.reports);
                    setChecked(true);
                } else {
                    handleSnackbarOpen('Error fetching reports', 'error');
                }
            } catch (error) {
                console.error('Fetch error:', error);
                handleSnackbarOpen('Error fetching reports', 'error');
            }
            if (refresh) {
                setRefresh(false);
            }
        };
        fetchReports();
    }, [addReport, refresh]);

    // Add validation
    useEffect(() => {
        const checkAddSubmitDisabled = () => {
            const isNameValid = reportName && checkNameUniqueness(reportName);
            const isPathValid = reportPath && validateUrl(reportPath);
            const isApiKeyValid = apiKey && apiKey.trim() !== '';
            
            if (isNameValid && isPathValid && isApiKeyValid) {
                setAddSubmitDisabled(false);
            } else {
                setAddSubmitDisabled(true);
            }
        };
        checkAddSubmitDisabled();
    }, [reportName, reportPath, apiKey, allReports]);

    // Edit validation
    useEffect(() => {
        const checkEditSubmitDisabled = () => {
            const isNameValid = editReportName && checkNameUniqueness(editReportName, selectedReport?.id);
            const isPathValid = editReportPath && validateUrl(editReportPath);
            
            if (isNameValid && isPathValid) {
                setEditSubmitDisabled(false);
            } else {
                setEditSubmitDisabled(true);
            }
        };
        checkEditSubmitDisabled();
    }, [editReportName, editReportPath, selectedReport, allReports]);

    const handleAddStartDateInterval = (index, value) => {
        const newRequiredFields = [...requiredFields];
        newRequiredFields[index].default_value = value;
        setRequiredFields(newRequiredFields);
    }

    const createPayload = () => {
        const filteredParams = requiredFields.filter(param => param.field_name !== '' && param.field_type !== '' && param.default_value !== '');
        return {
            name: reportName,
            path: reportPath,
            required_params: filteredParams,
            api_key: apiKey
        };
    };

    const createSqlPayload = () => {
        return {
            name: reportName,
            query: reportQuery,
            database: sqlDatabase
        };
    };


    const handleSnackbarOpen = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleDeleteReport = async (index) => {
        const response = await instance.post(`/reports/${allReports[index].id}/delete`, {}, {withCredentials: true});
        if (response.status === 200) {
            const updatedReports = allReports.filter((_report, i) => i !== index);
            setAllReports(updatedReports);
            handleSnackbarOpen('Report deleted successfully', 'success');
        } else {
            handleSnackbarOpen('Error deleting report', 'error');
        }
    };

    const handleDeleteReportClick = (index) => {
        setReportToDelete({ index, report: allReports[index] });
        setDeleteConfirmOpen(true);
    };

    const handleDeleteReportConfirm = () => {
        if (reportToDelete) {
            handleDeleteReport(reportToDelete.index);
            setDeleteConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const payload = createPayload();
        try {
            const response = await instance.post('/reports/add', payload, {withCredentials: true});
            if (response.status === 200) {
                handleSnackbarOpen('Report added successfully', 'success');
                setOpenAddReport(false);
                setReportName('');
                setReportPath('');
                setApiKey('');
                setRequiredFields([
                    {field_name: 'start_date', field_type: 'date', default_value: ''},
                    {field_name: 'end_date', field_type: 'date', default_value: ''}
                ]);
                setRefresh(true);
            } else if (response.status > 200 && response.status < 400) {
                handleSnackbarOpen(response.data.data.message, 'success');
                setOpenAddReport(false);
                setReportName('');
                setReportPath('');
                setApiKey('');
                setRequiredFields([
                    {field_name: 'start_date', field_type: 'date', default_value: ''},
                    {field_name: 'end_date', field_type: 'date', default_value: ''}
                ]);
                setRefresh(true);
            } else {
                handleSnackbarOpen('Error adding report', 'error');
            }
        } catch (error) {
            console.error('Submission error:', error);
            handleSnackbarOpen('Error adding report', 'error');
        }
    };

    const handleSqlFormSubmit = async (e) => {
        e.preventDefault();
        const payload = createSqlPayload();
        try {
            const response = await instance.post(
                '/middleware/report',
                payload,
                { withCredentials: true }
            );
            if (response.status === 200) {
                handleSnackbarOpen('Report added successfully', 'success');
                setOpenAddSqlReport(false);
                setReportName('');
                setReportQuery('');
                setSqlDatabase('');
                setRefresh(true);
            } else if (response.status > 200 && response.status < 400) {
                handleSnackbarOpen(response.data.data.message, 'success');
                setOpenAddSqlReport(false);
                setReportName('');
                setReportQuery('');
                setSqlDatabase('');
                setRefresh(true);
            } else {
                handleSnackbarOpen('Error adding report', 'error');
            }
        } catch (error) {
            console.error('Submission error:', error);
            handleSnackbarOpen('Error adding report', 'error');
        }
    };

    const handleRequiredFieldChange = (index, field, value) => {
        const newRequiredFields = [...requiredFields];
        newRequiredFields[index][field] = value;
        setRequiredFields(newRequiredFields);
    };

    const addRequiredField = () => {
        setRequiredFields([...requiredFields, {field_name: '', field_type: ''}]); 
    };

    const removeRequiredField = (index) => {
        const newRequiredFields = requiredFields.filter((_, i) => i !== index);
        setRequiredFields(newRequiredFields);
    };

    // Edit Report Functions
    const handleEditReport = (report) => {
        setSelectedReport(report);
        setEditReportName(report.name);
        setEditReportPath(report.path);
        setEditApiKey(report.api_key || '');
        setEditRequiredFields(report.required_params || []);
        setEditReportDialogOpen(true);
    };

    const handleEditReportClear = () => {
        setEditReportName('');
        setEditReportPath('');
        setEditApiKey('');
        setEditRequiredFields([]);
        setSelectedReport(null);
        setEditReportDialogOpen(false);
    };

    const isMiddlewareReport = (path) => {
        return path && path.includes('reporting-middleware.medigaplife.io/dynamic/run/');
    };

    const validateUrl = (url) => {
        return url.startsWith('http://') || url.startsWith('https://');
    };

    const validateEmail = (email) => {
        return email.includes('@');
    };

    const checkNameUniqueness = (name, currentId = null) => {
        return !allReports.some(report => report.name === name && report.id !== currentId);
    };

    const handleEditRequiredFieldChange = (index, field, value) => {
        const newRequiredFields = [...editRequiredFields];
        newRequiredFields[index][field] = value;
        setEditRequiredFields(newRequiredFields);
    };

    const addEditRequiredField = () => {
        setEditRequiredFields([...editRequiredFields, { field_name: '', field_type: '' }]);
    };

    const removeEditRequiredField = (index) => {
        const newRequiredFields = editRequiredFields.filter((_, i) => i !== index);
        setEditRequiredFields(newRequiredFields);
    };

    const handleEditReportSubmit = async () => {
        try {
            const payload = {
                name: editReportName,
                path: editReportPath,
                required_params: editRequiredFields.filter(param => param.field_name !== '' && param.field_type !== '')
            };

            // Only include api_key if it has a value
            if (editApiKey && editApiKey.trim() !== '') {
                payload.api_key = editApiKey;
            }

            const response = await instance.post(`/reports/${selectedReport.id}/edit`, payload, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('Report updated successfully', 'success');
                setEditReportDialogOpen(false);
                setRefresh(true);
            } else {
                handleSnackbarOpen('Failed to update report', 'error');
            }
        } catch (error) {
            console.error('Error updating report:', error);
            handleSnackbarOpen('Error updating report', 'error');
        }
    };

    return (
        <Grid2 container justifyContent="center" alignItems="center">
            <Grid2 item xs={12} md={6} lg={4}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '20px',
                    gap: '20px',
                    width: '100%'
                }}>
                    <h2 style={{
                        fontFamily: "Lato"
                    }}>Report Management</h2>
                    <Button variant="contained" color="primary" onClick={handleClick} style={{ alignSelf: 'flex-end' }}>
                        {openAddReport ? 'Close' : 'Add Report'}
                    </Button>
                    <Dialog open={openAddReport} onClose={handleReportClear}>
                        <DialogTitle>Add a New Report</DialogTitle>
                        <form onSubmit={handleFormSubmit}>
                            <DialogContent>
                                <DialogContentText>
                                    Please fill out the form below to add a new report.
                                </DialogContentText>
                                <TextField 
                                    fullWidth
                                    type="text" 
                                    label="Report Name" 
                                    variant="outlined"
                                    value={reportName}
                                    onChange={(e) => setReportName(e.target.value)}
                                    helperText={!checkNameUniqueness(reportName) && reportName ? "Name must be unique" : "Enter a unique name for the report."}
                                    required 
                                    margin="dense"
                                    error={!checkNameUniqueness(reportName) && reportName}
                                />
                                <TextField
                                    fullWidth
                                    type="text" 
                                    label="Report Path" 
                                    variant="outlined"
                                    value={reportPath}
                                    onChange={(e) => setReportPath(e.target.value)}
                                    helperText={!validateUrl(reportPath) && reportPath ? "Must start with http:// or https://" : "Enter the path to the report."}
                                    required
                                    margin="dense"
                                    error={!validateUrl(reportPath) && reportPath}
                                />
                                <TextField
                                    fullWidth
                                    type="text" 
                                    label="API Key" 
                                    variant="outlined"
                                    value={apiKey}
                                    //TODO: Add non-caching mechanism for API KEY
                                    onChange={(e) => setApiKey(e.target.value)}
                                    helperText="Enter the API key for the report." 
                                    required
                                    margin="dense"
                                />
                                {requiredFields.map((param, index) => (
                                    <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <TextField
                                            fullWidth
                                            type="text"
                                            label={`Parameter ${index + 1} Name`}
                                            variant="outlined"
                                            value={param.field_name}
                                            onChange={(e) => handleRequiredFieldChange(index, 'field_name', e.target.value)}
                                            margin="dense"
                                        />
                                        <TextField
                                            fullWidth
                                            type="text"
                                            label={`Parameter ${index + 1} Type`}
                                            variant="outlined"
                                            value={param.field_type}
                                            onChange={(e) => handleRequiredFieldChange(index, 'field_type', e.target.value)}
                                            margin="dense"
                                        />
                                        <Button variant="contained" color="secondary" onClick={() => removeRequiredField(index)}>Remove</Button>
                                    </div>
                                ))}
                                <Button variant="contained" color="primary" onClick={addRequiredField} style={{ marginTop: '10px' }}>Add Required Field</Button>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleReportClear} color="primary">
                                    Cancel
                                </Button>
                                <Button type="submit" color="primary" disabled={addSubmitDisabled}>
                                    Add Report
                                </Button>
                            </DialogActions>
                        </form>
                    </Dialog>
                    <Button variant='contained' onClick={handleClickSql} style={{ alignSelf: 'flex-end' }}>
                        {openAddSqlReport ? 'Close' : 'Add SQL Report'}
                    </Button>
                    <Dialog open={openAddSqlReport} onClose={handleSqlClear}> 
                        <DialogTitle>Add a SQL Report</DialogTitle>
                        <form onSubmit={handleSqlFormSubmit}>
                            <DialogContent>
                                <DialogContentText>
                                    Please fill out the form below to add a new SQL report.
                                </DialogContentText>
                                <TextField
                                    fullWidth
                                    type="text"
                                    label="Report Name"
                                    variant="outlined"
                                    value={reportName}
                                    onChange={(e) => setReportName(e.target.value)}
                                    helperText="Enter a unique name for the report."
                                    required
                                    margin="dense"
                                />
                                <TextField
                                    fullWidth
                                    type="text"
                                    label="SQL Query"
                                    variant="outlined"
                                    rows={10}
                                    multiline
                                    value={reportQuery}
                                    onChange={(e) => setReportQuery(e.target.value)}
                                    helperText="Enter the SQL query for the report."
                                    required
                                    margin="dense"
                                />
                                <FormControl fullWidth variant="outlined" margin="dense">
                                    <InputLabel id="database-select-label">SQL Database</InputLabel>
                                    <Select
                                        labelId="database-select-label"
                                        id="database-select"
                                        value={sqlDatabase}
                                        onChange={(e) => setSqlDatabase(e.target.value)}
                                        label="SQL Database"
                                        >
                                        <MenuItem value="MGLCRM">MGLCRM</MenuItem>
                                        <MenuItem value="INFUTOR">INFUTOR</MenuItem>
                                        <MenuItem value="CALL_LOG">CALL_LOG</MenuItem>
                                        <MenuItem value="ILE">ILE</MenuItem>
                                        <MenuItem value="ILE_ZIP">ILE_ZIP</MenuItem>
                                        <MenuItem value="ENRICHMENT">ENRICHMENT</MenuItem>
                                        <MenuItem value="POSTGRES">POSTGRES</MenuItem>
                                        <MenuItem value="COMMISSIONS">COMMISSIONS</MenuItem>
                                        <MenuItem value="ABIS">ABIS</MenuItem>
                                        <MenuItem value="PINGTREE">PINGTREE</MenuItem>
                                    </Select>
                                </FormControl>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleSqlClear} color="primary">
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" color="primary">
                                    Add SQL Report
                                </Button>
                            </DialogActions>
                        </form>
                    </Dialog>
                    <Fade in={checked}>
                    <div className='report-management'>
                        {allReports.length > 0 && addReport === false && (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell> </TableCell>
                                        <TableCell>#</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Path</TableCell>
                                        <TableCell> </TableCell>
                                        <TableCell> </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {allReports.map((report, index) => (
                                        <TableRow key={index}>
                                            <TableCell><DescriptionIcon /></TableCell>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{report.name}</TableCell>
                                            <TableCell>{report.path}</TableCell>
                                            <TableCell>
                                                {isMiddlewareReport(report.path) ? (
                                                    <Tooltip title="Edit functionality for SQL reports is currently unavailable. Work In Progress">
                                                        <span>
                                                            <Button 
                                                                color="primary" 
                                                                disabled
                                                                startIcon={<EditIcon />}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <Button 
                                                        color="primary" 
                                                        onClick={() => handleEditReport(report)}
                                                        startIcon={<EditIcon />}
                                                    >
                                                        Edit
                                                    </Button>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Button color="error" onClick={() => handleDeleteReportClick(index)}>
                                                    Delete
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                    </Fade>
                </div>
            </Grid2>
            <ConfirmDialog
                open={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                    setReportToDelete(null);
                }}
                onConfirm={handleDeleteReportConfirm}
                title="Delete Report"
                resourceType="Report"
                resourceName={reportToDelete?.report?.name}
                requireTypeConfirm={true}
                confirmButtonText="Delete Report"
            />
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <Dialog open={editReportDialogOpen} onClose={handleEditReportClear} maxWidth="md" fullWidth>
                <DialogTitle>Edit Report</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Update the report information below.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        type="text"
                        label="Report Name"
                        variant="outlined"
                        value={editReportName}
                        onChange={(e) => setEditReportName(e.target.value)}
                        helperText={!checkNameUniqueness(editReportName, selectedReport?.id) && editReportName ? "Name must be unique" : "Enter a unique name for the report."}
                        required
                        margin="dense"
                        error={!checkNameUniqueness(editReportName, selectedReport?.id) && editReportName}
                    />
                    <TextField
                        fullWidth
                        type="text"
                        label="Report Path"
                        variant="outlined"
                        value={editReportPath}
                        onChange={(e) => setEditReportPath(e.target.value)}
                        helperText={!validateUrl(editReportPath) && editReportPath ? "Must start with http:// or https://" : "Enter the path to the report."}
                        required
                        margin="dense"
                        error={!validateUrl(editReportPath) && editReportPath}
                    />
                    <TextField
                        fullWidth
                        type="text"
                        label="API Key"
                        variant="outlined"
                        value={editApiKey}
                        onChange={(e) => setEditApiKey(e.target.value)}
                        helperText="Enter the API key for the report."
                        margin="dense"
                    />
                    {editRequiredFields.map((param, index) => (
                        <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
                            <TextField
                                fullWidth
                                type="text"
                                label={`Parameter ${index + 1} Name`}
                                variant="outlined"
                                value={param.field_name}
                                onChange={(e) => handleEditRequiredFieldChange(index, 'field_name', e.target.value)}
                                margin="dense"
                            />
                            <TextField
                                fullWidth
                                type="text"
                                label={`Parameter ${index + 1} Type`}
                                variant="outlined"
                                value={param.field_type}
                                onChange={(e) => handleEditRequiredFieldChange(index, 'field_type', e.target.value)}
                                margin="dense"
                            />
                            <Button variant="contained" color="secondary" onClick={() => removeEditRequiredField(index)}>Remove</Button>
                        </div>
                    ))}
                    <Button variant="contained" color="primary" onClick={addEditRequiredField} style={{ marginTop: '10px' }}>Add Required Field</Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditReportClear} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleEditReportSubmit} color="primary" disabled={editSubmitDisabled}>
                        Update Report
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid2>
    );
}

export default ReportManagement;
